import { useQuery } from '@apollo/client'
import { AddCircle, RemoveCircle } from '@mui/icons-material'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useMemo } from 'react'
import { GetRostersByIds } from 'shared/components/RostersAutocomplete/RostersAutocomplete.gql'
import { useCandidateSearchUrlState } from 'shared/hooks/candidateSearchUrlState'
import { useReferenceSearchesUrlState } from 'shared/hooks/useReferenceSearchesUrlState'
import ProcessStatusPill from 'recruiter/components/ProcessStatusPill'
import { type CandidateTableColumnDefinition } from 'recruiter/components/candidateTableColumns'

const EMPTY_ROSTERS: readonly never[] = []

export function useRosterSearchColumns(
    templateColumns: CandidateTableColumnDefinition[]
): CandidateTableColumnDefinition[] {
    const { refColumnRosterIds, setRefColumnRosterIds, removeRosterId } =
        useReferenceSearchesUrlState()
    const { filters } = useCandidateSearchUrlState()

    const refColumnSet = new Set(refColumnRosterIds)
    const suggestedRosterIds = filters.searchFilters
        .map((f) => f.rosterId)
        .filter((id): id is string => !!id && !refColumnSet.has(id))

    const { data: rostersData } = useQuery(GetRostersByIds, {
        variables: { ids: refColumnRosterIds },
        skip: refColumnRosterIds.length === 0,
    })

    // using EMPTY_ROSTERS for useMemo
    const refRosters = rostersData?.rostersByIds ?? EMPTY_ROSTERS

    // eslint-disable-next-line react-hooks/exhaustive-deps -- removeRosterId is a new ref every render
    // (closes over URL state) but is only used in onClick handlers, not during render.
    // refRosters changing guarantees a fresh closure when it actually matters.
    return useMemo(() => {
        const referenceColumns: CandidateTableColumnDefinition[] =
            refRosters.map((roster) => ({
                field: `ref_${roster.id}`,
                headerName: roster.rosterName,
                width: 150,
                sortable: false,
                renderHeader: () => (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        gap={1}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                // maxWidth: 'calc(100% - 32px)',
                            }}
                        >
                            {roster.rosterName}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation()
                                removeRosterId(roster.id)
                            }}
                            sx={{ p: 0.25 }}
                        >
                            <RemoveCircle
                                color="error"
                                sx={{ fontSize: '1rem' }}
                            />
                        </IconButton>
                    </Stack>
                ),
                renderCell: (params) => {
                    const refCandidate =
                        params.row.referenceRosterCandidates?.find(
                            (rrc: { rosterId: string }) =>
                                rrc.rosterId === roster.id
                        )
                    if (!refCandidate) return <>-</>
                    return (
                        <ProcessStatusPill
                            status={refCandidate.processStatus ?? undefined}
                            statusLabel={
                                refCandidate.processStatusLabel ?? 'N/A'
                            }
                            summaryGroup={
                                refCandidate.summaryGroup ?? undefined
                            }
                            summaryGroupLabel={
                                refCandidate.summaryGroupLabel ?? undefined
                            }
                        />
                    )
                },
            }))

        const processStatusIndex = templateColumns.findIndex(
            (col) => col.field === 'processStatus'
        )
        if (processStatusIndex === -1) {
            return [...templateColumns, ...referenceColumns]
        }

        const columns = [
            ...templateColumns.slice(0, processStatusIndex + 1),
            ...referenceColumns,
            ...templateColumns.slice(processStatusIndex + 1),
        ]

        if (suggestedRosterIds.length > 0) {
            const psCol = columns[processStatusIndex]
            columns[processStatusIndex] = {
                ...psCol,
                renderHeader: () => (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        gap={0.5}
                    >
                        <Typography variant="subtitle2">
                            {psCol.headerName}
                        </Typography>
                        <Tooltip title="Add filtered searches as reference columns">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setRefColumnRosterIds([
                                        ...refColumnRosterIds,
                                        ...suggestedRosterIds,
                                    ])
                                }}
                                sx={{ p: 0.25 }}
                            >
                                <AddCircle
                                    color="primary"
                                    sx={{ fontSize: '1rem' }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ),
            }
        }

        return columns
    }, [templateColumns, refRosters, suggestedRosterIds])
}
